/**
 * DriverApplicationForm - Comprehensive Driver Application
 * Multi-step form with document uploads, vehicle details, and verification
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  ArrowRight,
  Truck,
  User,
  FileText,
  Upload,
  Camera,
  Check,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Shield,
  Car,
  Bike,
  AlertTriangle
} from 'lucide-react';

// Steps
// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Steps - dynamically adjusted based on login status
const getSteps = (isLoggedIn) => {
  const baseSteps = [
    { id: 1, title: 'Personal Info', icon: User },
    { id: 2, title: 'Vehicle Details', icon: Car },
    { id: 3, title: 'Documents', icon: FileText },
    { id: 4, title: 'Review', icon: Check },
  ];
  // Only show "Create Account" step if user is NOT logged in
  if (!isLoggedIn) {
    baseSteps.push({ id: 5, title: 'Create Account', icon: Shield });
  }
  return baseSteps;
};

// Vehicle types
const VEHICLE_TYPES = [
  { id: 'bicycle', name: 'Bicycle', icon: '🚲', capacity: 'Small packages' },
  { id: 'motorcycle', name: 'Motorcycle', icon: '🏍️', capacity: 'Medium packages' },
  { id: 'car', name: 'Car', icon: '🚗', capacity: 'Large packages' },
  { id: 'van', name: 'Van', icon: '🚐', capacity: 'Bulk deliveries' },
  { id: 'truck', name: 'Truck', icon: '🚚', capacity: 'Heavy freight' }
];

// Parishes in Jamaica
const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

export default function DriverApplicationForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Dynamically get steps based on login status
  const STEPS = getSteps(!!user);
  const totalSteps = STEPS.length;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form validation errors
  const [errors, setErrors] = useState({});
  
  // Step 1: Personal Info
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [parish, setParish] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');

  // Step 5: Account Creation
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Step 2: Vehicle Details
  const [vehicleType, setVehicleType] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [ppvId, setPpvId] = useState('');
  const [hasInsurance, setHasInsurance] = useState(false);
  const [insuranceExpiry, setInsuranceExpiry] = useState('');
  
  // Step 3: Documents
  const [nationalId, setNationalId] = useState(null);
  const [nationalIdPreview, setNationalIdPreview] = useState(null);
  const [driversLicense, setDriversLicense] = useState(null);
  const [driversLicensePreview, setDriversLicensePreview] = useState(null);
  const [vehiclePhoto, setVehiclePhoto] = useState(null);
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [insuranceDoc, setInsuranceDoc] = useState(null);
  const [insuranceDocPreview, setInsuranceDocPreview] = useState(null);

  useEffect(() => {
    // Pre-fill from user data
    if (user) {
      setFullName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please select an image or PDF file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = file.type.startsWith('image/') ? reader.result : null;
      
      switch (type) {
        case 'nationalId':
          setNationalId(file);
          setNationalIdPreview(preview);
          break;
        case 'driversLicense':
          setDriversLicense(file);
          setDriversLicensePreview(preview);
          break;
        case 'vehiclePhoto':
          setVehiclePhoto(file);
          setVehiclePhotoPreview(preview);
          break;
        case 'selfie':
          setSelfie(file);
          setSelfiePreview(preview);
          break;
        case 'insurance':
          setInsuranceDoc(file);
          setInsuranceDocPreview(preview);
          break;
      }
    };
    reader.readAsDataURL(file);
  };

  // Compress image to reduce file size for phone camera photos
  const compressImage = useCallback(async (file, maxWidth = 1600, maxHeight = 1600, quality = 0.8) => {
    // If not an image, return as-is
    if (!file.type.startsWith('image/')) {
      return file;
    }
    
    // If already small enough (< 1MB), return as-is
    if (file.size < 1024 * 1024) {
      return file;
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file from blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              console.log(`Compressed ${file.name}: ${(file.size/1024/1024).toFixed(2)}MB -> ${(compressedFile.size/1024/1024).toFixed(2)}MB`);
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const uploadFile = async (file, docType) => {
    try {
      // Auto-compress images before upload
      let fileToUpload = file;
      if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
        toast.info(`Compressing ${docType.replace('_', ' ')}...`);
        fileToUpload = await compressImage(file);
      }
      
      // Final size check after compression
      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
      if (fileToUpload.size > MAX_SIZE) {
        throw new Error(`File still too large after compression. Please use a smaller image.`);
      }
      
      const formData = new FormData();
      formData.append('file', fileToUpload);
      
      const res = await api.post(`/security/verify/public-upload?doc_type=${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for uploads
      });
      return res.data.file_path;
    } catch (error) {
      console.error('Upload error:', error);
      // Check for specific error types
      if (error.response?.status === 413) {
        throw new Error('File too large. Please use a smaller image or take a lower resolution photo.');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('Upload failed - network error. Please check your connection and try again.');
      }
      if (error.message) {
        throw error; // Re-throw with original message
      }
      throw new Error(`Failed to upload ${docType.replace('_', ' ')}. Please try again.`);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!phone.trim()) newErrors.phone = 'Phone number is required';
        if (!parish) newErrors.parish = 'Parish is required';
        // Email validation
        if (email && !EMAIL_REGEX.test(email)) {
          newErrors.email = 'Enter a valid email address (example: name@gmail.com)';
        }
        break;
      case 2:
        if (!vehicleType) newErrors.vehicleType = 'Please select a vehicle type';
        if (vehicleType !== 'bicycle' && !licensePlate.trim()) newErrors.licensePlate = 'License plate is required';
        break;
      case 3:
        if (!nationalId) newErrors.nationalId = 'National ID is required';
        if (vehicleType !== 'bicycle' && !driversLicense) newErrors.driversLicense = "Driver's license is required";
        break;
      case 5:
        // Only validate if user is not logged in (creating account)
        if (!user) {
          if (!email || !email.trim()) {
            newErrors.email = 'Email is required to create an account';
          } else if (!EMAIL_REGEX.test(email)) {
            newErrors.email = 'Enter a valid email address (example: name@gmail.com)';
          }
          if (!password || password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
          }
          if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
          }
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : Object.values(newErrors)[0];
  };

  const nextStep = () => {
    const error = validateStep(currentStep);
    if (error) {
      toast.error(error);
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Parse backend error response
  const parseBackendError = (error) => {
    const detail = error.response?.data?.detail;
    
    // Handle structured error response
    if (typeof detail === 'object') {
      // Check for field-specific errors
      if (detail.fields && Array.isArray(detail.fields)) {
        return `${detail.message || 'Validation error'}: ${detail.fields.join(', ')}`;
      }
      if (detail.message) {
        return detail.message;
      }
    }
    
    // Handle string error
    if (typeof detail === 'string') {
      return detail;
    }
    
    return 'Failed to submit application. Please try again.';
  };

  const handleSubmit = async () => {
    // If user is already logged in, skip password validation
    if (!user) {
      // Validate email format
      if (!email || !EMAIL_REGEX.test(email)) {
        toast.error('Enter a valid email address (example: name@gmail.com)');
        return;
      }
      // Validate password
      if (!password || password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
    }

    setSaving(true);
    setUploading(true);
    
    try {
      // Upload documents
      const uploadedDocs = {};
      
      if (nationalId) {
        uploadedDocs.national_id = await uploadFile(nationalId, "national_id");
      }
      if (driversLicense) {
        uploadedDocs.drivers_license = await uploadFile(driversLicense, "drivers_license");
      }
      if (vehiclePhoto) {
        uploadedDocs.vehicle_photo = await uploadFile(vehiclePhoto, "vehicle_photo");
      }
      if (selfie) {
        uploadedDocs.selfie = await uploadFile(selfie, "selfie");
      }
      if (insuranceDoc) {
        uploadedDocs.insurance = await uploadFile(insuranceDoc, "insurance");
      }
      
      setUploading(false);
      
      // Different submission path for logged-in vs new users
      if (user) {
        // User is already logged in - submit application without creating account
        const response = await api.post("/ppv/apply/authenticated", {
          full_name: fullName,
          email: user.email || email,
          phone,
          trn: ppvId || null,
          parish,
          ppv_badge_number: ppvId || "PENDING",
          date_of_birth: dateOfBirth,
          address,
          emergency_contact: emergencyContact,
          emergency_phone: emergencyPhone,
          vehicle_type: vehicleType,
          vehicle_make: vehicleMake,
          vehicle_model: vehicleModel,
          vehicle_year: vehicleYear ? parseInt(vehicleYear) : null,
          vehicle_color: vehicleColor,
          license_plate: licensePlate,
          has_insurance: hasInsurance,
          insurance_expiry: insuranceExpiry,
          documents: uploadedDocs
        });
        
        toast.success("Application submitted successfully!");
        navigate("/driver/home");
      } else {
        // New user - create account + application
        const response = await api.post("/ppv/apply/public", {
          full_name: fullName,
          email,
          phone,
          trn: ppvId || null,
          parish,
          ppv_badge_number: ppvId || "PENDING",
          date_of_birth: dateOfBirth,
          address,
          emergency_contact: emergencyContact,
          emergency_phone: emergencyPhone,
          vehicle_type: vehicleType,
          vehicle_make: vehicleMake,
          vehicle_model: vehicleModel,
          vehicle_year: vehicleYear ? parseInt(vehicleYear) : null,
          vehicle_color: vehicleColor,
          license_plate: licensePlate,
          has_insurance: hasInsurance,
          insurance_expiry: insuranceExpiry,
          documents: uploadedDocs,
          password: password
        });
        
        // Store auth token and user info
        if (response.data.token) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));
        }
        
        toast.success("Application submitted! Account created successfully.");
        navigate("/driver/home");
      }
    } catch (error) {
      console.error("Submit error:", error);
      // Show error message - check if it's an upload error or API error
      if (error.message && !error.response) {
        // This is a client-side error (like upload failure)
        toast.error(error.message);
      } else {
        toast.error(parseBackendError(error));
      }
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#00A86B]" />
                Driver Application
              </h1>
              <p className="text-sm text-slate-400">Join the  delivery network</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <Progress value={progress} className="h-2 bg-slate-800" />
          <div className="flex justify-between mt-4">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted ? 'bg-[#00A86B]' :
                    isActive ? 'bg-[#00A86B]/20 border-2 border-[#00A86B]' :
                    'bg-slate-800'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className={`w-5 h-5 ${isActive ? 'text-[#00A86B]' : 'text-slate-500'}`} />
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${isActive ? 'text-white' : 'text-slate-500'}`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Step 1: Personal Info */}
        {currentStep === 1 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-[#00A86B]" />
              Personal Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="bg-slate-900 border-slate-700 mt-1"
                  data-testid="driver-fullname-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Date of Birth *</Label>
                  <Input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="bg-slate-900 border-slate-700 mt-1"
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    data-testid="driver-dob-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Parish *</Label>
                  <select
                    value={parish}
                    onChange={(e) => setParish(e.target.value)}
                    className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                    data-testid="driver-parish-select"
                  >
                    <option value="">Select parish...</option>
                    {PARISHES.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (876) 000-0000"
                    className="bg-slate-900 border-slate-700 pl-10"
                    data-testid="driver-phone-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email {!user && '*'}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      // Clear error when user types
                      if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                    }}
                    placeholder="your@email.com"
                    className={`bg-slate-900 border-slate-700 pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="driver-email-input"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="text-slate-300">Street Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Your full address"
                    className="bg-slate-900 border-slate-700 pl-10 min-h-[80px]"
                    data-testid="driver-address-input"
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h3 className="font-medium mb-4 text-slate-300">Emergency Contact</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Contact Name</Label>
                    <Input
                      value={emergencyContact}
                      onChange={(e) => setEmergencyContact(e.target.value)}
                      placeholder="Emergency contact name"
                      className="bg-slate-900 border-slate-700 mt-1"
                      data-testid="driver-emergency-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Contact Phone</Label>
                    <Input
                      type="tel"
                      value={emergencyPhone}
                      onChange={(e) => setEmergencyPhone(e.target.value)}
                      placeholder="Emergency phone"
                      className="bg-slate-900 border-slate-700 mt-1"
                      data-testid="driver-emergency-phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Vehicle Details */}
        {currentStep === 2 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Car className="w-5 h-5 text-[#00A86B]" />
              Vehicle Details
            </h2>
            
            <div className="space-y-6">
              {/* Vehicle Type Selection */}
              <div>
                <Label className="text-slate-300 mb-3 block">Vehicle Type *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {VEHICLE_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setVehicleType(type.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        vehicleType === type.id
                          ? 'border-[#00A86B] bg-[#00A86B]/10'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`vehicle-type-${type.id}`}
                    >
                      <span className="text-2xl">{type.icon}</span>
                      <p className="font-medium mt-2">{type.name}</p>
                      <p className="text-xs text-slate-400">{type.capacity}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {vehicleType && vehicleType !== 'bicycle' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">License Plate *</Label>
                      <Input
                        value={licensePlate}
                        onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                        placeholder="ABC 1234"
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-license-plate-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">PPV ID (if applicable)</Label>
                      <Input
                        value={ppvId}
                        onChange={(e) => setPpvId(e.target.value)}
                        placeholder="PPV-001"
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-ppv-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Vehicle Make</Label>
                      <Input
                        value={vehicleMake}
                        onChange={(e) => setVehicleMake(e.target.value)}
                        placeholder="e.g., Toyota"
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-vehicle-make-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Vehicle Model</Label>
                      <Input
                        value={vehicleModel}
                        onChange={(e) => setVehicleModel(e.target.value)}
                        placeholder="e.g., Corolla"
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-vehicle-model-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Year</Label>
                      <Input
                        type="number"
                        value={vehicleYear}
                        onChange={(e) => setVehicleYear(e.target.value)}
                        placeholder="2020"
                        min="1990"
                        max={new Date().getFullYear() + 1}
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-vehicle-year-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Color</Label>
                      <Input
                        value={vehicleColor}
                        onChange={(e) => setVehicleColor(e.target.value)}
                        placeholder="e.g., Silver"
                        className="bg-slate-900 border-slate-700 mt-1"
                        data-testid="driver-vehicle-color-input"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-300">Vehicle Insurance</Label>
                        <p className="text-sm text-slate-400">Do you have valid vehicle insurance?</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setHasInsurance(!hasInsurance)}
                        className={`w-14 h-8 rounded-full transition-all ${
                          hasInsurance ? 'bg-[#00A86B]' : 'bg-slate-700'
                        }`}
                      >
                        <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                          hasInsurance ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {hasInsurance && (
                      <div className="mt-4">
                        <Label className="text-slate-300">Insurance Expiry Date</Label>
                        <Input
                          type="date"
                          value={insuranceExpiry}
                          onChange={(e) => setInsuranceExpiry(e.target.value)}
                          className="bg-slate-900 border-slate-700 mt-1"
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="driver-insurance-expiry-input"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {/* Step 3: Documents */}
        {currentStep === 3 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00A86B]" />
              Required Documents
            </h2>
            
            <div className="space-y-6">
              {/* National ID */}
              <div>
                <Label className="text-slate-300 mb-2 block">National ID (TRN Card) *</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {nationalIdPreview ? (
                      <img src={nationalIdPreview} alt="ID" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileSelect(e, 'nationalId')}
                      className="hidden"
                      id="national-id-upload"
                    />
                    <label htmlFor="national-id-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload ID
                        </span>
                      </Button>
                    </label>
                    {nationalId && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {nationalId.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Driver's License */}
              {vehicleType !== 'bicycle' && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Driver's License *</Label>
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                      {driversLicensePreview ? (
                        <img src={driversLicensePreview} alt="License" className="w-full h-full object-cover" />
                      ) : (
                        <Car className="w-8 h-8 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileSelect(e, 'driversLicense')}
                        className="hidden"
                        id="license-upload"
                      />
                      <label htmlFor="license-upload">
                        <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload License
                          </span>
                        </Button>
                      </label>
                      {driversLicense && (
                        <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {driversLicense.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Vehicle Photo */}
              {vehicleType && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Vehicle Photo</Label>
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                      {vehiclePhotoPreview ? (
                        <img src={vehiclePhotoPreview} alt="Vehicle" className="w-full h-full object-cover" />
                      ) : (
                        <Car className="w-8 h-8 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect(e, 'vehiclePhoto')}
                        className="hidden"
                        id="vehicle-upload"
                      />
                      <label htmlFor="vehicle-upload">
                        <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                          <span>
                            <Camera className="w-4 h-4 mr-2" />
                            Upload Photo
                          </span>
                        </Button>
                      </label>
                      {vehiclePhoto && (
                        <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {vehiclePhoto.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Selfie */}
              <div>
                <Label className="text-slate-300 mb-2 block">Selfie with ID (Recommended)</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {selfiePreview ? (
                      <img src={selfiePreview} alt="Selfie" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={(e) => handleFileSelect(e, 'selfie')}
                      className="hidden"
                      id="selfie-upload"
                    />
                    <label htmlFor="selfie-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Camera className="w-4 h-4 mr-2" />
                          Take Selfie
                        </span>
                      </Button>
                    </label>
                    {selfie && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {selfie.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Insurance Document */}
              {hasInsurance && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Insurance Certificate</Label>
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                      {insuranceDocPreview ? (
                        <img src={insuranceDocPreview} alt="Insurance" className="w-full h-full object-cover" />
                      ) : (
                        <Shield className="w-8 h-8 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileSelect(e, 'insurance')}
                        className="hidden"
                        id="insurance-upload"
                      />
                      <label htmlFor="insurance-upload">
                        <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Insurance
                          </span>
                        </Button>
                      </label>
                      {insuranceDoc && (
                        <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {insuranceDoc.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div className="text-sm text-yellow-400">
                    <p className="font-medium">Document Guidelines:</p>
                    <ul className="mt-1 space-y-1 text-yellow-400/80">
                      <li>• All documents must be clear and readable</li>
                      <li>• Information must match your registration</li>
                      <li>• Expired documents will be rejected</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Check className="w-5 h-5 text-[#00A86B]" />
              Review Application
            </h2>
            
            <div className="space-y-6">
              {/* Personal Info Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#00A86B]" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-400">Name:</p>
                  <p>{fullName}</p>
                  <p className="text-slate-400">Date of Birth:</p>
                  <p>{dateOfBirth}</p>
                  <p className="text-slate-400">Phone:</p>
                  <p>{phone}</p>
                  <p className="text-slate-400">Parish:</p>
                  <p>{parish}</p>
                </div>
              </div>
              
              {/* Vehicle Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4 text-[#00A86B]" />
                  Vehicle Details
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-400">Type:</p>
                  <p>{VEHICLE_TYPES.find(v => v.id === vehicleType)?.name || vehicleType}</p>
                  {licensePlate && (
                    <>
                      <p className="text-slate-400">License Plate:</p>
                      <p>{licensePlate}</p>
                    </>
                  )}
                  {vehicleMake && (
                    <>
                      <p className="text-slate-400">Make/Model:</p>
                      <p>{vehicleMake} {vehicleModel}</p>
                    </>
                  )}
                  <p className="text-slate-400">Insurance:</p>
                  <p>{hasInsurance ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              {/* Documents Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#00A86B]" />
                  Documents Uploaded
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    {nationalId ? <Check className="w-4 h-4 text-[#00A86B]" /> : <X className="w-4 h-4 text-red-400" />}
                    National ID
                  </p>
                  {vehicleType !== 'bicycle' && (
                    <p className="flex items-center gap-2">
                      {driversLicense ? <Check className="w-4 h-4 text-[#00A86B]" /> : <X className="w-4 h-4 text-red-400" />}
                      Driver's License
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    {vehiclePhoto ? <Check className="w-4 h-4 text-[#00A86B]" /> : <span className="w-4 h-4 text-slate-500">○</span>}
                    Vehicle Photo
                  </p>
                  <p className="flex items-center gap-2">
                    {selfie ? <Check className="w-4 h-4 text-[#00A86B]" /> : <span className="w-4 h-4 text-slate-500">○</span>}
                    Selfie with ID
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl">
                <p className="text-sm text-[#00A86B]">
                  By submitting this application, you confirm that all information provided is accurate and agree to 's driver terms and conditions.
                </p>
              </div>
            </div>
          </Card>
        )}


        {/* Step 5: Account Creation */}
        {currentStep === 5 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00A86B]" />
              Create Your Account
            </h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl">
                <p className="text-sm text-[#00A86B]">
                  Create a password to access your driver portal and track your application status.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    disabled
                    className="bg-slate-900/50 border-slate-700 text-slate-400"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Create Password *</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="bg-slate-900 border-slate-700"
                    data-testid="password-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Confirm Password *</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="bg-slate-900 border-slate-700"
                    data-testid="confirm-password-input"
                  />
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-2 text-slate-300">What happens next?</h3>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Your application will be reviewed within 2-3 business days</li>
                  <li>• You can login to check your application status anytime</li>
                  <li>• We will notify you via email when approved</li>
                </ul>
              </div>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex gap-4 mt-6">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className="border-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          
          {/* Show Submit on final step, or on step 4 if user is logged in */}
          {(currentStep === totalSteps) || (user && currentStep === 4) ? (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
              data-testid="submit-driver-application-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploading ? 'Uploading documents...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={nextStep}
              className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
